/**
 * The orchestrator: owns the ENU frame, drives the scheduler, feeds the
 * renderer, and answers "how high is the ground here?".
 *
 * Still engine-free — it talks to a `WorldRenderer` and a `TileLoader`.
 */

import type { LodConfig, WorldConfig } from '../config.js';
import type { PlacedVectorChunk } from './build/placement.js';
import { placeVectorChunk, pristineOf, restoreTerrain } from './build/placement.js';
import type { TerrainMeshData } from './build/terrainmesh.js';
import type { VectorChunkData } from './build/vectorfeatures.js';
import type { GeoOrigin, GeoPoint } from './geo/enu.js';
import { enuDistance, enuToGeo, geoToEnu, makeOrigin } from './geo/enu.js';
import type { TileKey } from './geo/tilekey.js';
import { tileKeyToString } from './geo/tilekey.js';
import { estimateMeshBytes, sampleMeshHeight } from './heightsample.js';
import type { WorldRenderer } from './renderer.js';
import type { DesiredTile, ViewerState } from './stream/lod.js';
import type { TileLoader } from './stream/scheduler.js';
import { TileScheduler } from './stream/scheduler.js';

export interface WorldStats {
  originLat: number;
  originLon: number;
  active: number;
  pending: number;
  inFlight: number;
  cached: number;
  cacheBytes: number;
  failed: number;
  rebases: number;
  vectorActive: number;
  vectorPending: number;
  vectorDeferred: number;
}

/**
 * Places vector chunks off the main thread. Given the chunk, its terrain and
 * that terrain's grid as built, resolves with the placed chunk and — when
 * the roads reshaped it — the terrain's new grid, positions and normals.
 */
export interface Placer {
  place(data: VectorChunkData, terrain: TerrainMeshData, pristine: Float32Array, dayOfYear: number): Promise<{
    placed: PlacedVectorChunk;
    terrain: {
      gridHeights: Float32Array; positions: Float32Array; normals: Float32Array;
      minHeight: number; maxHeight: number;
    } | undefined;
  }>;
}

/** Most placements applied in one frame; more wait for the next. */
const APPLY_BUDGET_MS = 2;

export class World {
  /** Placements finished off-thread, waiting to be applied a few per frame. */
  private readonly placed: Array<() => void> = [];
  /** A version per tile: a placement that finishes after a newer one began, or the tile went, is dropped. */
  private readonly placeVersion = new Map<string, number>();
  private placing = 0;
  private readonly config: WorldConfig;
  private readonly renderer: WorldRenderer;
  private readonly scheduler: TileScheduler<TerrainMeshData>;
  private readonly vectorScheduler: TileScheduler<VectorChunkData> | undefined;
  /**
   * Vector chunks whose terrain has not arrived yet.
   *
   * Features cannot be placed without a surface to place them on, and the two
   * streams race: a small vector tile often lands before the elevation tile
   * covering the same ground. Holding them here and flushing when the terrain
   * arrives is what stops a forest being dropped on the floor at y=0.
   */
  private readonly deferredVector = new Map<string, VectorChunkData>();
  /** Drawn chunks, so ground height can be sampled from the visible surface. */
  private readonly chunks = new Map<string, TerrainMeshData>();
  /** Vector tiles whose buildings are Lantmäteriet's, for the credit line. */
  private readonly lmBuildingTiles = new Set<string>();
  private origin: GeoOrigin;
  private rebases = 0;

  constructor(
    config: WorldConfig,
    renderer: WorldRenderer,
    loader: TileLoader<TerrainMeshData>,
    vectorLoader?: TileLoader<VectorChunkData>,
    /** Place vector chunks in a worker. Without one, placement is synchronous. */
    private readonly placer?: Placer,
  ) {
    this.config = config;
    this.renderer = renderer;
    this.origin = makeOrigin(config.spawn.lat, config.spawn.lon);
    this.renderer.setOrigin(this.origin);

    this.scheduler = new TileScheduler<TerrainMeshData>({
      loader,
      concurrency: config.concurrency.elevation,
      memoryBudgetBytes: config.memoryBudgetBytes,
      sizeOf: estimateMeshBytes,
      events: {
        onReady: (tile, mesh) => this.onChunkReady(tile, mesh),
        onDropped: (key) => this.onChunkDropped(key),
        onError: (key, error) => {
          console.warn(`[world] tile ${tileKeyToString(key)} failed:`, error);
        },
      },
      onEvict: (key) => this.chunks.delete(key),
    });

    if (vectorLoader !== undefined) {
      this.vectorScheduler = new TileScheduler<VectorChunkData>({
        loader: vectorLoader,
        concurrency: config.concurrency.vector,
        memoryBudgetBytes: config.vectorMemoryBudgetBytes,
        sizeOf: estimateVectorBytes,
        events: {
          onReady: (tile, data) => this.onVectorReady(data),
          onDropped: (key) => this.onVectorDropped(key),
          onError: (key, error) => {
            console.warn(`[world] vector tile ${tileKeyToString(key)} failed:`, error);
          },
        },
      });
    }
  }

  get currentOrigin(): GeoOrigin {
    return this.origin;
  }

  /**
   * Day of year, 0-based, for placing features in season. Set by the caller
   * from its clock; the world has no clock of its own.
   */
  dayOfYear = 172;

  get stats(): WorldStats {
    const s = this.scheduler.stats;
    const v = this.vectorScheduler?.stats;
    return {
      ...s,
      originLat: this.origin.lat,
      originLon: this.origin.lon,
      rebases: this.rebases,
      vectorActive: v?.active ?? 0,
      vectorPending: (v?.pending ?? 0) + (v?.inFlight ?? 0),
      vectorDeferred: this.deferredVector.size + this.placing + this.placed.length,
    };
  }

  /** Call once per frame with where the player is. */
  /**
   * Terrain detail to stream instead of the config's, while set: the top-down
   * view needs nothing out to the horizon, only the ground round the player.
   */
  lodOverride: LodConfig | undefined;

  update(viewer: ViewerState): void {
    // Apply finished placements, a frame's budget at a time: each is a mesh
    // build and a GPU upload, and all at once would be the freeze again.
    const start = performance.now();
    while (this.placed.length > 0) {
      this.placed.shift()!();
      if (performance.now() - start > APPLY_BUDGET_MS) break;
    }
    this.maybeRebase(viewer);
    this.scheduler.update(viewer, this.lodOverride ?? this.config.lod, this.origin);
    this.vectorScheduler?.update(viewer, this.config.vectorLod, this.origin);
  }

  /**
   * Re-anchor the frame when the player has wandered far from the origin, to
   * keep the tangent plane's east-west scale error small.
   *
   * Chunks are not translated: each one is rebuilt from its own tile key by the
   * renderer, because a constant translation cannot express the difference
   * between two frames whose east axes are scaled by different cosines.
   */
  private maybeRebase(viewer: ViewerState): void {
    const offset = geoToEnu(this.origin, viewer.lat, viewer.lon);
    if (enuDistance(offset, { east: 0, north: 0 }) < this.config.rebaseThreshold) return;
    this.origin = makeOrigin(viewer.lat, viewer.lon);
    this.rebases++;
    this.renderer.setOrigin(this.origin);
  }

  private onChunkReady(tile: DesiredTile, mesh: TerrainMeshData): void {
    const id = tileKeyToString(tile.key);
    this.chunks.set(id, mesh);
    this.renderer.addTerrainChunk(mesh);

    // Terrain for a waiting vector chunk has arrived; place it now.
    const waiting = this.deferredVector.get(id);
    if (waiting !== undefined) {
      this.deferredVector.delete(id);
      this.placeVector(waiting, mesh);
    }
  }

  private onChunkDropped(key: TileKey): void {
    const id = tileKeyToString(key);
    this.chunks.delete(id);
    this.renderer.removeTerrainChunk(key);
    // Vector features are placed against a specific terrain chunk, so they go
    // with it rather than floating where the ground used to be.
    this.deferredVector.delete(id);
    this.renderer.removeVectorChunk(key);
  }

  /** Rebuild a vector tile in place, e.g. once its building tags arrive. */
  refreshVector(key: TileKey): void {
    this.vectorScheduler?.refresh(key);
  }

  private onVectorReady(data: VectorChunkData): void {
    const id = tileKeyToString(data.key);
    if (data.lmBuildings === true) this.lmBuildingTiles.add(id);
    else this.lmBuildingTiles.delete(id);
    const terrain = this.chunks.get(id);
    if (terrain === undefined) {
      this.deferredVector.set(id, data);
      return;
    }
    this.placeVector(data, terrain);
  }

  /**
   * Place a vector chunk on its terrain. Placing reshapes the terrain under
   * the roads (see `build/roadprofile.ts`), so the terrain mesh is redrawn
   * first when it changed — and the player, sampling the same grid, stands
   * on the new ground at once.
   */
  private placeVector(data: VectorChunkData, terrain: TerrainMeshData): void {
    if (this.placer === undefined) {
      const placed = placeVectorChunk(data, terrain, this.dayOfYear);
      if (placed.terrainChanged) this.updateTerrain(terrain);
      this.renderer.addVectorChunk(placed);
      return;
    }
    const id = tileKeyToString(data.key);
    const version = (this.placeVersion.get(id) ?? 0) + 1;
    this.placeVersion.set(id, version);
    this.placing++;
    this.placer.place(data, terrain, pristineOf(terrain), this.dayOfYear).then((result) => {
      if (result.terrain !== undefined) {
        const reshaped = result.terrain;
        this.placed.push(() => {
          if (this.placeVersion.get(id) !== version || this.chunks.get(id) !== terrain) return;
          terrain.gridHeights.set(reshaped.gridHeights);
          terrain.positions.set(reshaped.positions);
          terrain.normals.set(reshaped.normals);
          terrain.minHeight = reshaped.minHeight;
          terrain.maxHeight = reshaped.maxHeight;
          this.updateTerrain(terrain);
        });
      }
      this.placed.push(() => {
        // Still the same terrain, and no newer placement or removal since.
        if (this.placeVersion.get(id) !== version || this.chunks.get(id) !== terrain) return;
        this.renderer.addVectorChunk(result.placed);
      });
    }, (error: unknown) => {
      console.warn(`[world] placing ${id} failed:`, error);
    }).finally(() => { this.placing--; });
  }

  /** Redraw a chunk whose heights changed, in place where the renderer can. */
  private updateTerrain(terrain: TerrainMeshData): void {
    if (this.renderer.updateTerrainHeights !== undefined) this.renderer.updateTerrainHeights(terrain);
    else this.renderer.addTerrainChunk(terrain);
  }

  private onVectorDropped(key: TileKey): void {
    const id = tileKeyToString(key);
    this.lmBuildingTiles.delete(id);
    this.placeVersion.set(id, (this.placeVersion.get(id) ?? 0) + 1);
    this.deferredVector.delete(id);
    this.renderer.removeVectorChunk(key);
    // The roads are gone, so the cuttings and embankments go with them.
    const terrain = this.chunks.get(id);
    if (terrain !== undefined && restoreTerrain(terrain)) this.updateTerrain(terrain);
  }

  /**
   * Ground elevation at a geographic point, or `undefined` if no chunk covering
   * it is loaded yet.
   *
   * Sampled from the *drawn* mesh, and along the same diagonal the mesh is
   * triangulated on, so anything standing on the result — the player, a
   * scattered tree — sits exactly on the visible surface rather than hovering
   * above or sinking into it.
   */
  heightAt(lat: number, lon: number): number | undefined {
    const best = this.terrainAt(lat, lon);
    if (best === undefined) return undefined;
    const point = geoToEnu(this.origin, lat, lon);
    return sampleMeshHeight(best, point.east - best.originEast, best.originNorth - point.north);
  }

  /** Whether the loaded vector tile's buildings are Lantmäteriet's (`VectorChunkData.lmBuildings`). */
  hasLmBuildings(key: TileKey): boolean {
    return this.lmBuildingTiles.has(tileKeyToString(key));
  }

  /** The finest loaded terrain chunk covering a geographic point, if any. */
  terrainAt(lat: number, lon: number): TerrainMeshData | undefined {
    const point = geoToEnu(this.origin, lat, lon);
    let best: TerrainMeshData | undefined;
    for (const mesh of this.chunks.values()) {
      const x = point.east - mesh.originEast;
      const z = mesh.originNorth - point.north;
      if (x < 0 || z < 0 || x > mesh.width || z > mesh.depth) continue;
      // Several zoom levels can never overlap, but during a LOD transition a
      // chunk may briefly linger; prefer the finest one covering the point.
      if (best === undefined || mesh.key.z > best.key.z) best = mesh;
    }
    return best;
  }

  /** Where the player is, as real-world coordinates, from a local position. */
  toGeo(east: number, north: number): GeoPoint {
    return enuToGeo(this.origin, east, north);
  }

  /** The inverse, for placing something given its coordinates. */
  toLocal(lat: number, lon: number): { east: number; north: number } {
    return geoToEnu(this.origin, lat, lon);
  }

  dispose(): void {
    this.scheduler.dispose();
    this.vectorScheduler?.dispose();
    this.chunks.clear();
    this.deferredVector.clear();
  }
}

/** Retained bytes of a vector chunk, for the cache budget. */
export function estimateVectorBytes(data: VectorChunkData): number {
  return (
    data.treePositions.byteLength +
    data.treeRotations.byteLength +
    data.treeScales.byteLength +
    data.treeSpecies.byteLength +
    data.treePaints.byteLength +
    data.landcover.byteLength +
    data.roadAttributes.byteLength +
    data.waterPositions.byteLength +
    data.waterIndices.byteLength +
    data.waterRanges.byteLength
  );
}

export { estimateMeshBytes, sampleMeshHeight } from './heightsample.js';
